import { localeAmount, parseNum, sentenceCase } from "@/utils/format";

/**
 * `getXReportData` — the original's module **`53034`**, ported.
 *
 * This is the whole X Report. `GET /analytics/x-report/…` answers an
 * enum-keyed object of flat aggregate rows; this turns it into the per-operator
 * report cards the drawer draws, plus the "Combined Report" that sums them.
 *
 * The transform is in two halves, and both are the original's:
 *
 * | half | symbol | what it does |
 * |---|---|---|
 * | `flatten` | `_` | walks the 13 read keys and emits one flat entry per row, keyed by staff |
 * | `combine` | `g` | groups those into `channels[] → subChannels[]`, derives the eight money totals, and sums the lot into `combined` |
 *
 * plus `finalizeChannels` (`m`), which sorts and flags.
 *
 * ## Things that look like bugs and are not
 *
 * - **`VOID_ITEMS` is never read.** It is the 14th key of the enum and the
 *   slice holds it, but the transform has no branch for it. Confirmed by
 *   grepping the whole of module `53034`: zero occurrences. Not an omission
 *   here.
 * - **A row whose staff id never appeared in `TOTAL_SALES` is dropped**, every
 *   time (`if (null != r)` guards every loop). An operator with a tip but no
 *   sale does not exist as far as this report is concerned.
 * - **`grossSale` adds the discounts back on** (`TS.grossAmount + discounts`)
 *   while `netSale` does not — that is what the two subtitles mean by "incl."
 *   and "excl. Discounts, Complimentary".
 * - **The COMPLIMENTARY and CANCELLED sub-channels are struck through and
 *   subtracted from their channel's own TOTAL row**, rather than being left
 *   out of it. The reader sees what was rung up and what it came off.
 * - **Per-staff totals are scaled to currency units, and the combined row sums
 *   the ALREADY-SCALED values** — so the combined figures are sums of rounded
 *   numbers, not a rounded sum. Reproduced: changing it would make the
 *   combined report disagree with adding up the cards on screen.
 */

/* -------------------------------------------------------------------------- */
/* the enum and the wire shape                                                */
/* -------------------------------------------------------------------------- */

/** `X_REPORT_TYPE_ENUM` (module `30065`), in its declaration order. */
export const X_REPORT_TYPE_ENUM = {
  TOTAL_SALES: "TOTAL_SALES",
  SURCHARGE: "SURCHARGE",
  SERVICE_CHARGE: "SERVICE_CHARGE",
  DELIVERY_CHARGE: "DELIVERY_CHARGE",
  TIP: "TIP",
  ORDER_DISCOUNT: "ORDER_DISCOUNT",
  TOTAL_DISCOUNT: "TOTAL_DISCOUNT",
  TAX_AMOUNT: "TAX_AMOUNT",
  GST: "GST",
  TOTAL_PAYMENT_SALES: "TOTAL_PAYMENT_SALES",
  SALES_BY_PLATFORM: "SALES_BY_PLATFORM",
  ORDER_STATUS: "ORDER_STATUS",
  COVERS: "COVERS",
  VOID_ITEMS: "VOID_ITEMS",
} as const;

export type XReportType =
  (typeof X_REPORT_TYPE_ENUM)[keyof typeof X_REPORT_TYPE_ENUM];

export const ALL_X_REPORT_TYPES: XReportType[] = Object.values(
  X_REPORT_TYPE_ENUM,
) as XReportType[];

/** One aggregate row. Which optional fields are present depends on the key. */
export interface XReportRow {
  staff_id?: string | null;
  staff_name?: string | null;
  grossAmount?: number;
  count?: number;
  /** `TOTAL_SALES` only. */
  totalItemSales?: number;
  totalTaxInvoiceProviderCharges?: number;
  /** `TOTAL_PAYMENT_SALES` only. */
  payment_type?: string;
  totalDiscountAmount?: number;
  totalDiscountCount?: number;
  platform?: string;
  status?: string;
  discount?: number;
  is_discount_percentage?: boolean;
  tip_amount?: number;
  service_fee?: number;
  is_service_fee_percentage?: boolean;
  delivery_cost?: number;
  surcharge?: number;
  is_surcharge_percentage?: boolean;
  gst?: number;
  covers?: number;
}

/** The slice payload: every key an array. */
export type XReportData = Record<XReportType, XReportRow[]>;

export const EMPTY_X_REPORT_DATA = (): XReportData =>
  ALL_X_REPORT_TYPES.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {} as XReportData);

/* -------------------------------------------------------------------------- */
/* the rendered shape                                                         */
/* -------------------------------------------------------------------------- */

export interface XReportSubChannel {
  _id: string;
  ref: string | number;
  title: string;
  count: number;
  amount: number;
  sortIndex: number;
  bold?: boolean;
  strikeThrough?: boolean;
}

export interface XReportFooterDetail {
  title: string;
  count: number;
  amount: number;
  sortIndex: number;
}

export interface XReportChannel {
  _id: string;
  ref: string;
  title: string;
  sortIndex: number;
  subChannels: XReportSubChannel[];
  footerDetails: XReportFooterDetail[];
}

export interface XReportCard {
  _id: string;
  title: string;
  netSale: number;
  grossSale: number;
  netWithoutGst: number;
  totalOrders: number;
  unpaidOrders: number;
  unpaidAmount: number;
  itemSales: number;
  netWithoutServiceFee: number;
  taxInvoiceProviderCharges: number;
  amountCollected: number;
  channels: XReportChannel[];
  grossSaleSubtitle: string;
  netSaleSubtitle: string;
  itemSalesSubtitle: string;
  netWithoutGstSubtitle: string;
  netWithoutServiceFeeSubtitle: string;
}

/** `r6` — the default card, and what the drawer shows before the first run. */
export const EMPTY_X_REPORT_CARD: XReportCard = {
  _id: "combined",
  title: "Combined Report",
  netSale: 0,
  grossSale: 0,
  netWithoutGst: 0,
  totalOrders: 0,
  unpaidOrders: 0,
  unpaidAmount: 0,
  itemSales: 0,
  netWithoutServiceFee: 0,
  taxInvoiceProviderCharges: 0,
  amountCollected: 0,
  channels: [],
  grossSaleSubtitle: "",
  netSaleSubtitle: "",
  itemSalesSubtitle: "",
  netWithoutGstSubtitle: "",
  netWithoutServiceFeeSubtitle: "",
};

/* -------------------------------------------------------------------------- */
/* the constants the sorters index against                                    */
/* -------------------------------------------------------------------------- */

/** `p` — the synthetic per-channel total row's `ref`. */
const TOTAL_REF = "TOTAL";
/** `f` — the synthetic "everything not an order discount" discount row. */
const ITEM_DISCOUNTS_REF = "ITEM_DISCOUNTS";
/** `h` — the staff key an unattributed row falls back to. */
const BY_CUSTOMERS_KEY = "by_customers";
/** `d` — the types whose sub-channels are COUNTED rather than listed. */
const COUNTED_TYPES: string[] = [X_REPORT_TYPE_ENUM.COVERS];

/** `ALL_PAYMENT_TYPE_NAMES` (module `77498`), in declaration order. */
export const ALL_PAYMENT_TYPE_NAMES = [
  "UNPAID",
  "PAY_LATER",
  "CASH",
  "CARD",
  "JAZZCASH_PAY",
  "EASYPAISA_PAY",
  "RAAST_PAY",
  "TERMINAL",
  "MARKED_PAID",
  "COMPLIMENTARY",
];

/** `ALL_ORDER_STATUS_NAMES`, in declaration order. */
const ALL_ORDER_STATUS_NAMES = [
  "UNCONFIRMED",
  "PLACED",
  "PREPARING",
  "READY",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "COMPLETED",
  "CANCELLED",
];

/**
 * `ALL_PLATFORMS` — `Object.values(PLATFORMS_ENUM)`, which is what the
 * platform sub-channels are ordered by. Only the ordering matters, and an
 * unlisted platform falls to `200 + index`, so the list does not have to be
 * exhaustive to be correct — but it is the original's own, in its own order.
 */
const ALL_PLATFORMS = [
  "POS_WALK-IN",
  "POS_TAKE-AWAY",
  "POS_DELIVERY",
  "POS_DINE-IN",
  "POS_DRIVE-THRU",
  "POS_KIOSK",
  "WEB_TAKE-AWAY",
  "WEB_DELIVERY",
  "WEB_DINE-IN",
  "APP_TAKE-AWAY",
  "APP_DELIVERY",
  "APP_DINE-IN",
];

/**
 * `convertPriceFromCents` (module `98574`), verbatim — including the
 * negative-zero normalisation, which is why a fully refunded line reads `0`
 * and not `-0`.
 */
export const convertPriceFromCents = (value: number): number => {
  const result = parseFloat((value / 100).toFixed(2));
  return Object.is(result, -0) ? 0 : result;
};

/**
 * `getGSTName` — the tax label, which is BOTH the GST channel's title and part
 * of the `netWithoutGstSubtitle`. Module `7423`, verbatim.
 */
export const getGSTName = (
  countryAlpha2?: string | null,
  taxInvoiceProvider?: string | null,
): string => {
  if (countryAlpha2 === "PK") {
    switch (taxInvoiceProvider) {
      case "FBR":
        return "GST";
      case "PRA":
        return "PST";
      case "SRB":
        return "SST";
      case "KPRA":
        return "Tax";
      default:
        return "Tax";
    }
  }
  if (countryAlpha2 === "AE") return "VAT";
  if (countryAlpha2 === "CA") return "HST";
  if (countryAlpha2 === "US") return "Sales Tax";
  if (countryAlpha2 === "GB") return "VAT";
  if (countryAlpha2 === "AU") return "GST";
  if (countryAlpha2 === "NZ") return "GST";
  if (countryAlpha2 === "AF") return "VAT";
  if (countryAlpha2 === "ES") return "IVA";
  return "Tax";
};

/* -------------------------------------------------------------------------- */
/* `_` — flatten                                                              */
/* -------------------------------------------------------------------------- */

interface FlatEntry {
  _id: string;
  ref: string | number;
  type: string;
  staff_name: string;
  title: string;
  description: string | number;
  count: number;
  amount: number;
  sortIndex: number;
}

interface TotalSalesRow {
  staff_name?: string | null;
  count?: number;
  grossAmount?: number;
  totalItemSales?: number;
  totalTaxInvoiceProviderCharges?: number;
}

type FooterMap = Record<string, Record<string, XReportFooterDetail>>;

const num = (value?: number | null): number => Number(value ?? 0);

const flatten = (
  data: XReportData,
  currency: string | null | undefined,
  gstName: string,
) => {
  const byStaff: Record<string, FlatEntry[]> = {};
  const totalSales: Record<string, TotalSalesRow> = {};
  const names: Record<string, string> = {};

  const rows = (key: XReportType): XReportRow[] => {
    // The original indexes straight in and throws on a missing key. `:4001`
    // always answers the full enum-keyed object, but a partial response must
    // not take the drawer down with it.
    const value = data?.[key];
    return Array.isArray(value) ? value : [];
  };

  rows(X_REPORT_TYPE_ENUM.TOTAL_SALES).forEach((row) => {
    const name = row.staff_name ?? "By Customers";
    const key = row.staff_id ?? BY_CUSTOMERS_KEY;
    totalSales[key] = row;
    names[key] = name;
  });

  /** Every branch below shares this shape; only the four varying parts differ. */
  const push = (
    row: XReportRow,
    make: (staffName: string) => Omit<FlatEntry, "_id" | "staff_name">,
  ) => {
    const key = row.staff_id ?? BY_CUSTOMERS_KEY;
    const staffName = names[key];
    if (staffName == null) return;
    const entry: FlatEntry = { _id: key, staff_name: staffName, ...make(staffName) };
    if (byStaff[key] != null) byStaff[key].push(entry);
    else byStaff[key] = [entry];
  };

  rows(X_REPORT_TYPE_ENUM.SALES_BY_PLATFORM).forEach((row) =>
    push(row, () => ({
      ref: String(row.platform),
      title: "Platform Sales",
      description: sentenceCase(String(row.platform).replace(/_/g, " ")),
      type: X_REPORT_TYPE_ENUM.SALES_BY_PLATFORM,
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 1,
    })),
  );

  rows(X_REPORT_TYPE_ENUM.ORDER_STATUS).forEach((row) =>
    push(row, () => ({
      ref: String(row.status),
      type: X_REPORT_TYPE_ENUM.ORDER_STATUS,
      title: "Order Status",
      description: sentenceCase(String(row.status).replace(/_/g, " ")),
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 2,
    })),
  );

  // Payments additionally seed the FOOTER map: the per-payment-type discount
  // sub-totals, which hang off the ORDER_DISCOUNT channel rather than off
  // Payments. That indirection is the original's.
  const footers: FooterMap = {};
  rows(X_REPORT_TYPE_ENUM.TOTAL_PAYMENT_SALES).forEach((row) => {
    const key = row.staff_id ?? BY_CUSTOMERS_KEY;
    if (names[key] == null) return;
    push(row, () => ({
      ref: String(row.payment_type),
      type: X_REPORT_TYPE_ENUM.TOTAL_PAYMENT_SALES,
      title: "Payments",
      description: sentenceCase(String(row.payment_type).replace(/_/g, " ")),
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 3,
    }));
    footers[key] ??= {};
    const paymentType = String(row.payment_type);
    const existing = footers[key][paymentType];
    if (existing != null) {
      // Note the asymmetry, which is the original's: an entry is only CREATED
      // when the discount is positive, but once it exists it accumulates
      // whatever arrives, zero included.
      existing.amount += num(row.totalDiscountAmount);
      existing.count += num(row.totalDiscountCount);
    } else if (num(row.totalDiscountAmount) > 0) {
      footers[key][paymentType] = {
        amount: num(row.totalDiscountAmount),
        count: num(row.totalDiscountCount),
        sortIndex: 0,
        title: sentenceCase(paymentType.replace(/_/g, " ")),
      };
    }
  });

  // ORDER_DISCOUNT is tracked per staff so TOTAL_DISCOUNT can have it
  // subtracted — what is left over is the ITEM discounts, which have no row of
  // their own anywhere.
  const orderDiscounts: Record<string, { count: number; amount: number }> = {};
  rows(X_REPORT_TYPE_ENUM.ORDER_DISCOUNT).forEach((row) => {
    const key = row.staff_id ?? BY_CUSTOMERS_KEY;
    if (names[key] == null) return;
    const value = convertPriceFromCents(num(row.discount));
    const label = row.is_discount_percentage
      ? `${value}%`
      : localeAmount(currency, value);
    const amount = num(row.grossAmount);
    const bucket = (orderDiscounts[key] ??= { count: 0, amount: 0 });
    bucket.count += num(row.count);
    bucket.amount += amount;
    push(row, () => ({
      ref: label,
      type: X_REPORT_TYPE_ENUM.ORDER_DISCOUNT,
      title: "Discounts",
      description: label,
      count: num(row.count),
      amount,
      sortIndex: 4,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.TOTAL_DISCOUNT).forEach((row) => {
    const key = row.staff_id ?? BY_CUSTOMERS_KEY;
    if (names[key] == null) return;
    const already = orderDiscounts[key] ?? { count: 0, amount: 0 };
    const count = num(row.count) - already.count;
    const amount = num(row.grossAmount) - already.amount;
    if (amount <= 0) return;
    push(row, () => ({
      ref: ITEM_DISCOUNTS_REF,
      type: X_REPORT_TYPE_ENUM.ORDER_DISCOUNT,
      title: "Discounts",
      description: "Item Discounts",
      count: count || num(row.count),
      amount,
      sortIndex: 5,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.TIP).forEach((row) =>
    push(row, () => ({
      ref: num(row.tip_amount),
      type: X_REPORT_TYPE_ENUM.TIP,
      title: "Tips",
      description: localeAmount(
        currency,
        convertPriceFromCents(num(row.tip_amount)),
      ),
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 6,
    })),
  );

  rows(X_REPORT_TYPE_ENUM.SERVICE_CHARGE).forEach((row) => {
    const value = convertPriceFromCents(num(row.service_fee));
    const label = row.is_service_fee_percentage
      ? `${value}%`
      : localeAmount(currency, value);
    push(row, () => ({
      ref: label,
      type: X_REPORT_TYPE_ENUM.SERVICE_CHARGE,
      title: "Service Charges",
      description: label,
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 7,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.DELIVERY_CHARGE).forEach((row) => {
    const label = localeAmount(
      currency,
      convertPriceFromCents(num(row.delivery_cost)),
    );
    push(row, () => ({
      ref: label,
      type: X_REPORT_TYPE_ENUM.DELIVERY_CHARGE,
      title: "Delivery Charges",
      description: label,
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 8,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.SURCHARGE).forEach((row) => {
    const value = convertPriceFromCents(num(row.surcharge));
    const label = row.is_surcharge_percentage
      ? `${value}%`
      : localeAmount(currency, value);
    push(row, () => ({
      ref: label,
      type: X_REPORT_TYPE_ENUM.SURCHARGE,
      title: "Surcharge",
      description: label,
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 9,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.GST).forEach((row) => {
    // The GST channel is titled with the TENANT's tax name — "GST" in Pakistan
    // under FBR, "VAT" in the UAE — not with the word GST.
    const rate = convertPriceFromCents(num(row.gst));
    push(row, () => ({
      ref: rate,
      type: X_REPORT_TYPE_ENUM.GST,
      title: gstName,
      description: `${rate}%`,
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 10,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.TAX_AMOUNT).forEach((row) => {
    // Note the asymmetry: the LABEL is the scaled amount, the `amount` stays in
    // cents so the combiner can keep summing in one scale.
    const scaled = convertPriceFromCents(num(row.grossAmount));
    push(row, () => ({
      ref: scaled,
      type: X_REPORT_TYPE_ENUM.TAX_AMOUNT,
      title: "Item Tax",
      description: localeAmount(currency, scaled),
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 11,
    }));
  });

  rows(X_REPORT_TYPE_ENUM.COVERS).forEach((row) =>
    push(row, () => ({
      ref: num(row.covers),
      type: X_REPORT_TYPE_ENUM.COVERS,
      // A NUMBER description, which is what makes the sub-channel title read
      // "12 Covers" rather than just "12" — see `combine`.
      description: num(row.covers),
      title: "Covers",
      count: num(row.count),
      amount: num(row.grossAmount),
      sortIndex: 12,
    })),
  );

  // The payment-type footers are ordered by the platform's payment order.
  const footerDetailsByStaff: Record<
    string,
    Record<string, Record<string, XReportFooterDetail>>
  > = {};
  for (const staffKey of Object.keys(footers)) {
    const map = footers[staffKey];
    if (Object.keys(map).length === 0) continue;
    footerDetailsByStaff[staffKey] ??= {};
    let overflow = 0;
    const cloned: Record<string, XReportFooterDetail> = {};
    for (const paymentType of Object.keys(map)) {
      let index = ALL_PAYMENT_TYPE_NAMES.indexOf(paymentType);
      if (index === -1) {
        index = 200 + overflow;
      }
      overflow += 1;
      cloned[paymentType] = { ...map[paymentType], sortIndex: index };
    }
    footerDetailsByStaff[staffKey][X_REPORT_TYPE_ENUM.ORDER_DISCOUNT] = cloned;
  }

  return { byStaff, totalSales, footerDetailsByStaff };
};

/* -------------------------------------------------------------------------- */
/* `m` — sort and flag                                                        */
/* -------------------------------------------------------------------------- */

interface DraftChannel {
  _id: string;
  ref: string;
  title: string;
  sortIndex: number;
  subChannels: XReportSubChannel[];
  footerDetailsMap: Record<string, XReportFooterDetail>;
}

const finalizeChannels = (channels: DraftChannel[]): XReportChannel[] => {
  const out: XReportChannel[] = [];
  for (const draft of channels) {
    const channel: XReportChannel = {
      _id: draft._id,
      title: draft.title,
      ref: draft.ref,
      sortIndex: draft.sortIndex,
      subChannels: draft.subChannels,
      footerDetails: Object.values(draft.footerDetailsMap)
        .map((entry) => ({ ...entry, amount: convertPriceFromCents(entry.amount) }))
        .sort((a, b) => a.sortIndex - b.sortIndex),
    };
    const subs = channel.subChannels;
    // Alphabetical by `ref` first; the per-type rules below then override it.
    subs
      .sort((a, b) => (String(a.ref) > String(b.ref) ? 1 : -1))
      .forEach((entry, index) => {
        entry.sortIndex = index;
      });
    for (let index = 0; index < subs.length; index += 1) {
      const sub = subs[index];
      if (sub.ref === TOTAL_REF) {
        // COVERS has no meaningful money total, so its TOTAL row is removed —
        // while still being flagged on the way out, which is the original's own
        // splice-during-iteration.
        if (COUNTED_TYPES.includes(channel.ref)) subs.splice(index, 1);
        sub.sortIndex = 1e5;
        sub.bold = true;
      } else if (channel.ref === X_REPORT_TYPE_ENUM.SALES_BY_PLATFORM) {
        const found = ALL_PLATFORMS.indexOf(String(sub.ref));
        sub.sortIndex = found === -1 ? 200 + index : found;
      } else if (channel.ref === X_REPORT_TYPE_ENUM.TOTAL_PAYMENT_SALES) {
        const found = ALL_PAYMENT_TYPE_NAMES.indexOf(String(sub.ref));
        sub.sortIndex = found === -1 ? 200 + index : found;
        if (String(sub.ref) === "COMPLIMENTARY") {
          const totalIndex = subs.findIndex((entry) => entry.ref === TOTAL_REF);
          if (totalIndex !== -1) {
            subs[totalIndex].amount = parseNum(
              subs[totalIndex].amount - sub.amount,
            );
          }
          sub.strikeThrough = true;
        }
      } else if (channel.ref === X_REPORT_TYPE_ENUM.ORDER_STATUS) {
        const found = ALL_ORDER_STATUS_NAMES.indexOf(String(sub.ref));
        const resolved = found === -1 ? 200 + index : found;
        if (String(sub.ref) === "CANCELLED") {
          const totalIndex = subs.findIndex((entry) => entry.ref === TOTAL_REF);
          if (totalIndex !== -1) {
            subs[totalIndex].amount = parseNum(
              subs[totalIndex].amount - sub.amount,
            );
          }
          sub.strikeThrough = true;
        }
        sub.sortIndex = resolved;
      }
    }
    out.push(channel);
  }
  return out
    .map((channel) => ({
      ...channel,
      subChannels: channel.subChannels.sort((a, b) => a.sortIndex - b.sortIndex),
    }))
    .sort((a, b) => a.sortIndex - b.sortIndex);
};

/* -------------------------------------------------------------------------- */
/* `g` — combine                                                              */
/* -------------------------------------------------------------------------- */

type Subtitles = Pick<
  XReportCard,
  | "itemSalesSubtitle"
  | "grossSaleSubtitle"
  | "netSaleSubtitle"
  | "netWithoutServiceFeeSubtitle"
  | "netWithoutGstSubtitle"
>;

const combine = (
  flat: ReturnType<typeof flatten>,
  subtitles: Subtitles,
): { staffWiseData: XReportCard[]; combinedData: XReportCard } => {
  const { byStaff, totalSales, footerDetailsByStaff } = flat;
  const cards: XReportCard[] = [];
  const running = { ...EMPTY_X_REPORT_CARD, ...subtitles };
  /** The cross-staff channel accumulators — `f` (listed) and `h` (counted). */
  const merged: Record<string, DraftChannel & { subChannelMap: Record<string, XReportSubChannel> }> = {};
  const counted: Record<
    string,
    Omit<DraftChannel, "subChannels"> & {
      subChannel: { _id: string; ref: string | number; num: number; count: number; amount: number };
    }
  > = {};

  for (const staffKey of Object.keys(byStaff)) {
    const sales = totalSales[staffKey];
    if (!sales) continue;
    const entries = byStaff[staffKey];

    const grouped: Record<string, FlatEntry[]> = {};
    for (const entry of entries) {
      if (grouped[entry.type] != null) grouped[entry.type].push(entry);
      else grouped[entry.type] = [entry];
    }

    const totalOrders = num(sales.count);
    let netSale = num(sales.grossAmount);
    let discounts = 0;
    let gst = 0;
    let unpaidOrders = 0;
    let unpaidAmount = 0;
    let serviceCharge = 0;
    let deliveryCharge = 0;

    // Every group gets a synthetic "Total" row appended BEFORE anything reads
    // it, so the sums below can skip it by `ref !== TOTAL`.
    for (const type of Object.keys(grouped)) {
      const first = grouped[type][0];
      if (!first) continue;
      const summed = grouped[type].reduce(
        (acc, entry) => {
          acc.count += entry.count;
          acc.amount += entry.amount;
          return acc;
        },
        { count: 0, amount: 0 },
      );
      grouped[type].push({
        _id: `${first._id}-${TOTAL_REF}`,
        ref: TOTAL_REF,
        type: first.type,
        staff_name: first.staff_name,
        title: first.title,
        description: "Total",
        count: summed.count,
        amount: summed.amount,
        sortIndex: 1e5,
      });
    }

    const drafts: DraftChannel[] = [];
    for (const type of Object.keys(grouped)) {
      const group = grouped[type];
      const first = group[0];
      const footerDetailsMap = { ...(footerDetailsByStaff[staffKey]?.[type] ?? {}) };
      const draft: DraftChannel = {
        _id: `${first._id}-${first.type}`,
        ref: first.type,
        title: first.title,
        sortIndex: first.sortIndex,
        subChannels: [],
        footerDetailsMap,
      };
      const subChannels: XReportSubChannel[] = [];

      for (let index = 0; index < group.length; index += 1) {
        const entry = group[index];
        const label = String(entry.description).toUpperCase();
        if (entry.type === X_REPORT_TYPE_ENUM.GST && entry.ref !== TOTAL_REF) {
          gst += entry.amount;
        } else if (
          entry.type === X_REPORT_TYPE_ENUM.ORDER_DISCOUNT &&
          entry.ref !== TOTAL_REF
        ) {
          discounts += entry.amount;
        } else if (
          entry.type === X_REPORT_TYPE_ENUM.TOTAL_PAYMENT_SALES &&
          entry.ref !== TOTAL_REF
        ) {
          // The test is on the sentence-cased DESCRIPTION, not on `ref`.
          if (label === "UNPAID") {
            unpaidOrders += entry.count;
            unpaidAmount += entry.amount;
          } else if (label === "COMPLIMENTARY") {
            netSale -= entry.amount;
          }
        } else if (
          entry.type === X_REPORT_TYPE_ENUM.SERVICE_CHARGE &&
          entry.ref !== TOTAL_REF
        ) {
          serviceCharge += entry.amount;
        } else if (
          entry.type === X_REPORT_TYPE_ENUM.DELIVERY_CHARGE &&
          entry.ref !== TOTAL_REF
        ) {
          deliveryCharge += entry.amount;
        }

        const sub: XReportSubChannel = {
          _id: `${entry._id}-${entry.ref}`,
          ref: entry.ref,
          title:
            typeof entry.description === "string"
              ? entry.description
              : `${entry.description} ${sentenceCase(entry.type)}`,
          count: entry.count,
          amount: entry.amount,
          sortIndex: index,
        };

        if (COUNTED_TYPES.includes(type)) {
          if (typeof entry.description === "number") {
            const existing = counted[type];
            if (existing != null) {
              existing.subChannel.num += entry.description;
              existing.subChannel.count += entry.count;
              existing.subChannel.amount += entry.amount;
            } else {
              counted[type] = {
                _id: `${entry._id}-${entry.type}`,
                ref: entry.type,
                title: entry.title,
                sortIndex: entry.sortIndex,
                footerDetailsMap: {},
                subChannel: {
                  _id: `${entry._id}-${entry.ref}`,
                  ref: entry.ref,
                  num: entry.description,
                  count: entry.count,
                  amount: entry.amount,
                },
              };
            }
          }
          if (index === 0 && counted[type] != null) {
            const next = { ...counted[type].footerDetailsMap };
            for (const key of Object.keys(footerDetailsMap)) {
              if (next[key] != null) {
                next[key] = {
                  ...next[key],
                  amount: next[key].amount + footerDetailsMap[key].amount,
                  count: next[key].count + footerDetailsMap[key].count,
                };
              } else next[key] = footerDetailsMap[key];
            }
            counted[type].footerDetailsMap = next;
          }
        } else {
          const existing = merged[type];
          if (existing != null) {
            const key = String(entry.ref);
            if (existing.subChannelMap[key] != null) {
              existing.subChannelMap[key].amount += entry.amount;
              existing.subChannelMap[key].count += entry.count;
            } else {
              existing.subChannelMap[key] = { ...sub, sortIndex: index };
            }
          } else {
            merged[type] = {
              _id: `${entry._id}-${entry.type}`,
              ref: entry.type,
              title: entry.title,
              sortIndex: entry.sortIndex,
              footerDetailsMap: {},
              subChannels: [],
              subChannelMap: { [String(entry.ref)]: { ...sub, sortIndex: index } },
            };
          }
          if (index === 0) {
            const next = { ...merged[type].footerDetailsMap };
            for (const key of Object.keys(footerDetailsMap)) {
              if (next[key] != null) {
                next[key] = {
                  ...next[key],
                  amount: next[key].amount + footerDetailsMap[key].amount,
                  count: next[key].count + footerDetailsMap[key].count,
                };
              } else next[key] = footerDetailsMap[key];
            }
            merged[type].footerDetailsMap = next;
          }
        }

        // The per-staff copy is scaled here; the cross-staff copy is scaled at
        // the very end, so both sides sum in cents and round once.
        subChannels.push({ ...sub, amount: convertPriceFromCents(sub.amount) });
      }

      draft.subChannels = subChannels;
      drafts.push(draft);
    }

    const grossRaw = num(sales.grossAmount) + discounts;
    const withoutServiceFee = netSale - serviceCharge - deliveryCharge;
    const withoutGst = withoutServiceFee - gst;
    const itemSalesRaw = num(sales.totalItemSales) - discounts;
    const collected = netSale - unpaidAmount;

    const card: XReportCard = {
      ...subtitles,
      _id: staffKey,
      title: sales.staff_name ?? "",
      grossSale: convertPriceFromCents(grossRaw),
      netSale: convertPriceFromCents(netSale),
      unpaidAmount: convertPriceFromCents(unpaidAmount),
      netWithoutGst: convertPriceFromCents(withoutGst),
      itemSales: convertPriceFromCents(itemSalesRaw),
      netWithoutServiceFee: convertPriceFromCents(withoutServiceFee),
      taxInvoiceProviderCharges: convertPriceFromCents(
        num(sales.totalTaxInvoiceProviderCharges),
      ),
      amountCollected: convertPriceFromCents(collected),
      totalOrders,
      unpaidOrders,
      channels: drafts as unknown as XReportChannel[],
    };

    running.grossSale += card.grossSale;
    running.netSale += card.netSale;
    running.amountCollected += card.amountCollected;
    running.netWithoutGst += card.netWithoutGst;
    running.totalOrders += card.totalOrders;
    running.unpaidOrders += card.unpaidOrders;
    running.unpaidAmount += card.unpaidAmount;
    running.itemSales += card.itemSales;
    running.netWithoutServiceFee += card.netWithoutServiceFee;
    running.taxInvoiceProviderCharges += card.taxInvoiceProviderCharges;

    cards.push({ ...card, channelDrafts: drafts } as XReportCard & {
      channelDrafts: DraftChannel[];
    });
  }

  const staffWiseData = cards
    .map((card) => {
      const drafts = (card as XReportCard & { channelDrafts: DraftChannel[] })
        .channelDrafts;
      const finalized = finalizeChannels(drafts);
      const next = { ...card, channels: finalized } as XReportCard & {
        channelDrafts?: DraftChannel[];
      };
      delete next.channelDrafts;
      return next as XReportCard;
    })
    .sort((a, b) => b.netSale - a.netSale);

  // The counted channels (Covers) collapse into ONE sub-channel titled with
  // their running count — "37 Covers".
  Object.values(counted).forEach((entry) => {
    merged[entry._id] = {
      _id: entry._id,
      ref: entry.ref,
      title: entry.title,
      subChannels: [],
      subChannelMap: {
        [String(entry.subChannel.ref)]: {
          _id: entry.subChannel._id,
          ref: entry.subChannel.ref,
          title: `${entry.subChannel.num} ${sentenceCase(entry.title)}`,
          count: entry.subChannel.count,
          amount: entry.subChannel.amount,
          sortIndex: 0,
        },
      },
      sortIndex: entry.sortIndex,
      footerDetailsMap: entry.footerDetailsMap,
    };
  });

  const combinedDrafts: DraftChannel[] = Object.values(merged).map((entry) => ({
    _id: entry._id,
    ref: entry.ref,
    title: entry.title,
    sortIndex: entry.sortIndex,
    footerDetailsMap: entry.footerDetailsMap,
    subChannels: Object.values(entry.subChannelMap).map((sub) => ({
      ...sub,
      amount: convertPriceFromCents(sub.amount),
    })),
  }));

  return {
    staffWiseData,
    combinedData: {
      ...subtitles,
      _id: running._id,
      title: running.title,
      channels: finalizeChannels(combinedDrafts),
      grossSale: parseNum(running.grossSale),
      netSale: parseNum(running.netSale),
      netWithoutGst: parseNum(running.netWithoutGst),
      totalOrders: parseNum(running.totalOrders),
      unpaidAmount: parseNum(running.unpaidAmount),
      unpaidOrders: parseNum(running.unpaidOrders),
      itemSales: parseNum(running.itemSales),
      netWithoutServiceFee: parseNum(running.netWithoutServiceFee),
      taxInvoiceProviderCharges: parseNum(running.taxInvoiceProviderCharges),
      amountCollected: parseNum(running.amountCollected),
    },
  };
};

/* -------------------------------------------------------------------------- */
/* `y` — the entry point                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `getXReportData(sliceData, currency_symbol, country_alpha_2, tax_invoice_provider)`.
 *
 * The five subtitles are the original's, verbatim; only the last one is a
 * template, and its variable is the tenant's own tax name.
 */
export const getXReportData = (
  data: XReportData,
  currencySymbol?: string | null,
  countryAlpha2?: string | null,
  taxInvoiceProvider?: string | null,
): { staffWiseData: XReportCard[]; combinedData: XReportCard } => {
  const gstName = getGSTName(countryAlpha2, taxInvoiceProvider);
  const subtitles: Subtitles = {
    itemSalesSubtitle: "Items Amount - Discounts",
    grossSaleSubtitle: "incl. Discounts, Complimentary",
    netSaleSubtitle: "excl. Discounts, Complimentary",
    netWithoutServiceFeeSubtitle:
      "excl. Service Charges, Delivery Charges, Discounts, Complimentary",
    netWithoutGstSubtitle: `excl. ${gstName}, Service Charges, Delivery Charges, Discounts, Complimentary`,
  };
  return combine(flatten(data, currencySymbol, gstName), subtitles);
};

export default getXReportData;
